import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Star, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  'Land Clearing',
  'Mulching',
  'Excavation',
  'Grading',
  'Brush Removal',
  'Tree Removal',
  'Site Preparation',
  'Demolition',
  'Stump Removal'
];

export const ContractorProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    description: '',
    services: [],
    equipment: '',
    service_area: '',
    years_experience: '',
    license_number: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchReviews();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/contractors/${user.id}`);
      setProfile(response.data);
      setFormData({
        company_name: response.data.company_name || '',
        description: response.data.description || '',
        services: response.data.services || [],
        equipment: (response.data.equipment || []).join('\n'),
        service_area: response.data.service_area || '',
        years_experience: response.data.years_experience || '',
        license_number: response.data.license_number || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/contractor/${user.id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleService = (service) => {
    const newServices = formData.services.includes(service)
      ? formData.services.filter(s => s !== service)
      : [...formData.services, service];
    setFormData({ ...formData, services: newServices });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        company_name: formData.company_name || null,
        description: formData.description || null,
        services: formData.services,
        equipment: formData.equipment ? formData.equipment.split('\n').filter(e => e.trim()) : [],
        service_area: formData.service_area || null,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        license_number: formData.license_number || null
      };

      await axios.put(`${API}/contractors/profile`, payload);
      toast.success('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950" data-testid="contractor-profile-page">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/contractor/dashboard" 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-4xl text-white uppercase">Your Profile</h1>
          <div className="flex items-center gap-2 text-stone-400">
            <Star className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span className="font-heading text-2xl text-orange-500">{profile?.rating?.toFixed(1) || '0.0'}</span>
            <span>({profile?.total_reviews || 0} reviews)</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Business Information</h2>

            <div>
              <Label htmlFor="company_name" className="form-label">Company Name</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Your business name"
                className="mt-2 bg-stone-900 border-stone-700 h-12"
                data-testid="company-name"
              />
            </div>

            <div>
              <Label htmlFor="description" className="form-label">About Your Business</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Tell customers about your experience, capabilities, and what sets you apart..."
                className="mt-2 bg-stone-900 border-stone-700 min-h-32"
                data-testid="description"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="years_experience" className="form-label">Years in Business</Label>
                <Input
                  id="years_experience"
                  name="years_experience"
                  type="number"
                  value={formData.years_experience}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="years-experience"
                />
              </div>
              <div>
                <Label htmlFor="license_number" className="form-label">License Number</Label>
                <Input
                  id="license_number"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleChange}
                  placeholder="Your contractor license #"
                  className="mt-2 bg-stone-900 border-stone-700 h-12"
                  data-testid="license-number"
                />
              </div>
            </div>
          </div>

          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Services Offered</h2>
            
            <div className="flex flex-wrap gap-2">
              {SERVICE_OPTIONS.map((service) => (
                <button
                  key={service}
                  type="button"
                  onClick={() => toggleService(service)}
                  className={`px-4 py-2 rounded-sm border transition-colors ${
                    formData.services.includes(service)
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-900 border-stone-700 text-stone-400 hover:border-stone-600'
                  }`}
                  data-testid={`service-${service.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {service}
                </button>
              ))}
            </div>
          </div>

          <div className="industrial-card p-6 space-y-6">
            <h2 className="font-heading text-xl text-orange-500 uppercase">Equipment & Service Area</h2>

            <div>
              <Label htmlFor="equipment" className="form-label">Equipment (One per line)</Label>
              <Textarea
                id="equipment"
                name="equipment"
                value={formData.equipment}
                onChange={handleChange}
                placeholder="Bobcat T770&#10;CAT 308 Excavator&#10;Fecon Mulcher"
                className="mt-2 bg-stone-900 border-stone-700 min-h-32"
                data-testid="equipment"
              />
            </div>

            <div>
              <Label htmlFor="service_area" className="form-label">Service Area</Label>
              <Input
                id="service_area"
                name="service_area"
                value={formData.service_area}
                onChange={handleChange}
                placeholder="e.g., Central Texas, 100 mile radius from Austin"
                className="mt-2 bg-stone-900 border-stone-700 h-12"
                data-testid="service-area"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={saving}
            data-testid="save-profile-btn"
            className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wide text-lg rounded-sm"
          >
            {saving ? <Loader2 className="animate-spin" /> : <><Save className="mr-2" size={20} /> Save Profile</>}
          </Button>
        </form>

        {/* Reviews Section */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl text-white uppercase mb-6">Reviews</h2>
          
          {reviews.length === 0 ? (
            <div className="industrial-card p-8 text-center">
              <Star className="w-12 h-12 text-stone-600 mx-auto mb-3" />
              <p className="text-stone-400">No reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="industrial-card p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">{review.customer_name}</span>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating ? 'text-orange-500 fill-orange-500' : 'text-stone-600'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-stone-300">{review.comment}</p>
                  <p className="text-stone-500 text-sm mt-2">
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
